/**
 * Dashboard Widget Components
 * Phase 1: Common widgets for all employees
 * Created: January 29, 2026
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  ClipboardList, 
  Cake, 
  Megaphone, 
  AlertTriangle,
  CheckCircle2,
  Clock,
  ArrowRight,
  Package,
  FileText,
  ShoppingCart,
  Wrench,
  PartyPopper,
  CalendarHeart
} from 'lucide-react';

// ==================== HELPER FUNCTIONS ====================

const getScoreColor = (score, isNegative = true) => {
  if (isNegative) {
    if (score <= 10) return 'text-green-600';
    if (score <= 25) return 'text-yellow-600';
    if (score <= 40) return 'text-orange-600';
    return 'text-red-600';
  }
  if (score >= 90) return 'text-green-600';
  if (score >= 70) return 'text-yellow-600';
  if (score >= 50) return 'text-orange-600';
  return 'text-red-600';
};

const getSeverityBadge = (severity) => {
  switch(severity) {
    case 'extreme_critical': return 'bg-red-100 text-red-800 border-red-300';
    case 'critical': return 'bg-orange-100 text-orange-800 border-orange-300';
    case 'delayed': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getSeverityLabel = (severity) => {
  switch(severity) {
    case 'extreme_critical': return 'Extreme Critical';
    case 'critical': return 'Critical';
    case 'delayed': return 'Delayed';
    default: return 'On Track';
  }
};

// ==================== MY TASKS & SCORES WIDGET ====================

export const MyTasksScoresWidget = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="flex items-center justify-center h-full">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full border-red-200">
        <CardContent className="flex items-center justify-center h-full">
          <div className="text-red-500 text-sm">{error}</div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { current_tasks, weekly_scores, week_info } = data;

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-base">My Tasks & Scores</CardTitle>
          </div>
          <Badge variant="outline" className="text-xs">Week {week_info?.week_number}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Tasks Summary */}
        <div className="grid grid-cols-3 gap-2">
          <div className="text-center p-2 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{current_tasks?.total_pending || 0}</p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </div>
          <div className="text-center p-2 bg-red-50 rounded-lg">
            <p className="text-2xl font-bold text-red-600">{current_tasks?.total_overdue || 0}</p>
            <p className="text-xs text-muted-foreground">Overdue</p>
          </div>
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold">{weekly_scores?.total_tasks_due || 0}</p>
            <p className="text-xs text-muted-foreground">Due This Week</p>
          </div>
        </div>

        {/* Task Type Breakdown */}
        <div className="space-y-2">
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Delegation</span>
            <span>
              <span className="font-medium">{current_tasks?.delegation?.pending || 0}</span>
              <span className="text-muted-foreground"> pending, </span>
              <span className="font-medium text-red-600">{current_tasks?.delegation?.overdue || 0}</span>
              <span className="text-muted-foreground"> overdue</span>
            </span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Checklist</span>
            <span>
              <span className="font-medium">{current_tasks?.checklist?.pending || 0}</span>
              <span className="text-muted-foreground"> pending, </span>
              <span className="font-medium text-red-600">{current_tasks?.checklist?.overdue || 0}</span>
              <span className="text-muted-foreground"> overdue</span>
            </span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">FMS</span>
            <span>
              <span className="font-medium">{current_tasks?.fms?.pending || 0}</span>
              <span className="text-muted-foreground"> pending, </span>
              <span className="font-medium text-red-600">{current_tasks?.fms?.overdue || 0}</span>
              <span className="text-muted-foreground"> overdue</span>
            </span>
          </div>
        </div>

        {/* Weekly Scores (v2 Negative Scoring) */}
        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground mb-2">Weekly Performance (Lower = Better)</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="text-center p-2 bg-gray-50 rounded">
              <p className={`text-xl font-bold ${getScoreColor(weekly_scores?.pct_not_done || 0)}`}>
                {weekly_scores?.pct_not_done || 0}%
              </p>
              <p className="text-xs text-muted-foreground">Not Done</p>
            </div>
            <div className="text-center p-2 bg-gray-50 rounded">
              <p className={`text-xl font-bold ${getScoreColor(weekly_scores?.pct_not_done_on_time || 0)}`}>
                {weekly_scores?.pct_not_done_on_time || 0}%
              </p>
              <p className="text-xs text-muted-foreground">Not On Time</p>
            </div>
          </div>
        </div>

        {/* Escalations */}
        {(weekly_scores?.escalations?.extreme_critical > 0 || weekly_scores?.escalations?.critical > 0) && (
          <div className="flex gap-2">
            {weekly_scores?.escalations?.extreme_critical > 0 && (
              <Badge className="bg-red-100 text-red-800">
                {weekly_scores.escalations.extreme_critical} Extreme Critical
              </Badge>
            )}
            {weekly_scores?.escalations?.critical > 0 && (
              <Badge className="bg-orange-100 text-orange-800">
                {weekly_scores.escalations.critical} Critical
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== TODAY'S CELEBRATIONS WIDGET ====================

export const TodaysCelebrationsWidget = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="flex items-center justify-center h-full">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full border-red-200">
        <CardContent className="flex items-center justify-center h-full">
          <div className="text-red-500 text-sm">{error}</div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { birthdays, anniversaries, total_count } = data;

  return (
    <Card className="h-full bg-gradient-to-br from-pink-50 to-purple-50">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <PartyPopper className="w-5 h-5 text-pink-600" />
          <CardTitle className="text-base">Today&apos;s Celebrations</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {total_count === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            <CalendarHeart className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No celebrations today</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Birthdays */}
            {birthdays?.length > 0 && (
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <Cake className="w-4 h-4 text-pink-500" />
                  <span className="text-xs font-medium text-pink-600">Birthdays</span>
                </div>
                {birthdays.map((person, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm py-1">
                    <span className="text-lg">🎂</span>
                    <div>
                      <p className="font-medium">{person.name}</p>
                      <p className="text-xs text-muted-foreground">{person.department}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Work Anniversaries */}
            {anniversaries?.length > 0 && (
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <CalendarHeart className="w-4 h-4 text-purple-500" />
                  <span className="text-xs font-medium text-purple-600">Work Anniversaries</span>
                </div>
                {anniversaries.map((person, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm py-1">
                    <span className="text-lg">🎉</span>
                    <div>
                      <p className="font-medium">{person.name}</p>
                      <p className="text-xs text-muted-foreground">{person.years} years • {person.department}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== TODAY'S ANNOUNCEMENTS WIDGET ====================

export const TodaysAnnouncementsWidget = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="flex items-center justify-center h-full">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full border-red-200">
        <CardContent className="flex items-center justify-center h-full">
          <div className="text-red-500 text-sm">{error}</div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { announcements, total_count, quote_of_the_day } = data;

  return (
    <Card className="h-full bg-gradient-to-br from-yellow-50 to-orange-50">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-yellow-600" />
          <CardTitle className="text-base">Announcements</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {quote_of_the_day && (
          <div className="mb-3 p-2.5 bg-white/60 rounded-lg border border-amber-200/50">
            <p className="text-sm italic text-amber-900/80">&ldquo;{quote_of_the_day}&rdquo;</p>
          </div>
        )}
        {total_count === 0 ? (
          !quote_of_the_day && (
            <div className="text-center py-4 text-muted-foreground">
              <Megaphone className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No announcements today</p>
            </div>
          )
        ) : (
          <div className="space-y-2">
            {announcements.map((ann, idx) => (
              <div key={idx} className="p-2 bg-white/60 rounded-lg">
                <p className="font-medium text-sm">{ann.title}</p>
                {ann.content && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{ann.content}</p>
                )}
                {ann.created_at && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(ann.created_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== ACTION REQUIRED WIDGET ====================

export const ActionRequiredWidget = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="flex items-center justify-center h-full">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full border-red-200">
        <CardContent className="flex items-center justify-center h-full">
          <div className="text-red-500 text-sm">{error}</div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { actions, total_count, by_severity } = data;

  return (
    <Card className="h-full border-red-200 bg-gradient-to-br from-red-50 to-orange-50">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <CardTitle className="text-base">Action Required</CardTitle>
          </div>
          {total_count > 0 && (
            <Badge className="bg-red-100 text-red-800">{total_count}</Badge>
          )}
        </div>
        {/* Severity Summary */}
        {(by_severity?.extreme_critical > 0 || by_severity?.critical > 0) && (
          <div className="flex gap-1 mt-1">
            {by_severity?.extreme_critical > 0 && (
              <Badge className="bg-red-600 text-white text-xs">{by_severity.extreme_critical} Extreme</Badge>
            )}
            {by_severity?.critical > 0 && (
              <Badge className="bg-orange-500 text-white text-xs">{by_severity.critical} Critical</Badge>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {total_count === 0 ? (
          <div className="text-center py-4">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
            <p className="text-sm text-green-600 font-medium">All caught up!</p>
            <p className="text-xs text-muted-foreground">No pending actions</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {actions.slice(0, 10).map((action, idx) => (
              <div 
                key={idx} 
                className={`p-2 rounded-lg border ${
                  action.severity === 'extreme_critical' ? 'bg-red-100 border-red-300' :
                  action.severity === 'critical' ? 'bg-orange-100 border-orange-300' :
                  'bg-yellow-100 border-yellow-300'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{action.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={`text-xs ${getSeverityBadge(action.severity)}`}>
                        {action.days_overdue > 0 ? `${action.days_overdue}d overdue` : getSeverityLabel(action.severity)}
                      </Badge>
                      <span className="text-xs text-muted-foreground capitalize">{action.category}</span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="shrink-0 h-7 px-2">
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            {total_count > 10 && (
              <p className="text-xs text-center text-muted-foreground pt-2">
                +{total_count - 10} more items
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== PHASE 2 WIDGETS (Role-Specific) ====================

export const PendingGateEntriesWidget = ({ data, loading, error }) => {
  if (loading) return <WidgetSkeleton icon={Package} title="Pending Gate Entries" />;
  if (error) return <WidgetError error={error} />;
  if (!data) return null;

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-green-600" />
            <CardTitle className="text-base">Pending Gate Entries</CardTitle>
          </div>
          <Badge className="bg-green-100 text-green-800">{data.total_count}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-2">
          <div className="text-center p-2 bg-yellow-50 rounded">
            <p className="text-xl font-bold text-yellow-600">{data.summary?.pending_verification || 0}</p>
            <p className="text-xs text-muted-foreground">Awaiting Verification</p>
          </div>
          <div className="text-center p-2 bg-blue-50 rounded">
            <p className="text-xl font-bold text-blue-600">{data.summary?.pending_grn || 0}</p>
            <p className="text-xs text-muted-foreground">Pending GRN</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const PendingIndentsWidget = ({ data, loading, error }) => {
  if (loading) return <WidgetSkeleton icon={FileText} title="Pending Indents" />;
  if (error) return <WidgetError error={error} />;
  if (!data) return null;

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-600" />
            <CardTitle className="text-base">Pending Indents</CardTitle>
          </div>
          <Badge className="bg-purple-100 text-purple-800">{data.total_count}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-2">
          <div className="text-center p-2 bg-yellow-50 rounded">
            <p className="text-lg font-bold text-yellow-600">{data.summary?.pending_approval || 0}</p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </div>
          <div className="text-center p-2 bg-green-50 rounded">
            <p className="text-lg font-bold text-green-600">{data.summary?.approved_awaiting_po || 0}</p>
            <p className="text-xs text-muted-foreground">Approved</p>
          </div>
          <div className="text-center p-2 bg-blue-50 rounded">
            <p className="text-lg font-bold text-blue-600">{data.summary?.partial || 0}</p>
            <p className="text-xs text-muted-foreground">Partial</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const PendingPOsWidget = ({ data, loading, error }) => {
  if (loading) return <WidgetSkeleton icon={ShoppingCart} title="Pending POs" />;
  if (error) return <WidgetError error={error} />;
  if (!data) return null;

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-indigo-600" />
            <CardTitle className="text-base">Pending POs</CardTitle>
          </div>
          <Badge className="bg-indigo-100 text-indigo-800">{data.total_count}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-2">
          <div className="text-center p-2 bg-yellow-50 rounded">
            <p className="text-lg font-bold text-yellow-600">{data.summary?.pending_approval || 0}</p>
            <p className="text-xs text-muted-foreground">Approval</p>
          </div>
          <div className="text-center p-2 bg-green-50 rounded">
            <p className="text-lg font-bold text-green-600">{data.summary?.approved || 0}</p>
            <p className="text-xs text-muted-foreground">Approved</p>
          </div>
          <div className="text-center p-2 bg-blue-50 rounded">
            <p className="text-lg font-bold text-blue-600">{data.summary?.sent_to_supplier || 0}</p>
            <p className="text-xs text-muted-foreground">Sent</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const PendingJCIssuesWidget = ({ data, loading, error }) => {
  if (loading) return <WidgetSkeleton icon={Wrench} title="Pending JC Issues" />;
  if (error) return <WidgetError error={error} />;
  if (!data) return null;

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-orange-600" />
            <CardTitle className="text-base">Pending JC Issues</CardTitle>
          </div>
          <Badge className="bg-orange-100 text-orange-800">{data.total_count}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {data.total_count === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
            <p className="text-sm">All materials issued</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {data.items?.slice(0, 5).map((item, idx) => (
              <div key={idx} className="p-2 bg-orange-50 rounded text-sm">
                <p className="font-medium">{item.work_order_number}</p>
                <p className="text-xs text-muted-foreground">{item.item_name} • {item.process_name}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== PENDING OT COMPLETION REPORTS WIDGET ====================

export const PendingOTReportsWidget = ({ data, loading, error }) => {
  if (loading) {
    return <WidgetSkeleton icon={Clock} title="Pending OT Reports" />;
  }
  
  if (error) {
    return <WidgetError error={error} />;
  }
  
  const reports = data || [];
  
  if (reports.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-green-600" />
            <CardTitle className="text-base">OT Completion Reports</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-20 text-muted-foreground text-sm">
            <CheckCircle2 className="w-5 h-5 mr-2 text-green-500" />
            No pending completion reports
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="h-full border-amber-200 bg-amber-50/30">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" />
            <CardTitle className="text-base">OT Completion Reports</CardTitle>
          </div>
          <Badge variant="destructive" className="text-xs">
            {reports.length} pending
          </Badge>
        </div>
        <CardDescription className="text-xs">
          Submit completion reports for completed OT shifts
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {reports.slice(0, 3).map((report) => (
          <div 
            key={report.id} 
            className="p-2 bg-white rounded border border-amber-200 hover:border-amber-400 transition-colors"
          >
            <div className="flex justify-between items-start">
              <div>
                <div className="font-medium text-sm text-amber-700">{report.request_number}</div>
                <div className="text-xs text-gray-600">
                  {new Date(report.ot_date).toLocaleDateString('en-IN', { 
                    day: '2-digit', month: 'short', year: 'numeric' 
                  })} • {report.from_time} - {report.to_time}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {report.worker_count} workers • {report.unit_name}
                </div>
              </div>
              <Button 
                size="sm" 
                variant="outline"
                className="text-xs h-7 border-amber-400 text-amber-700 hover:bg-amber-100"
                onClick={() => window.location.href = '/production/ot-permission'}
              >
                Submit Report
              </Button>
            </div>
          </div>
        ))}
        {reports.length > 3 && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full text-amber-700"
            onClick={() => window.location.href = '/production/ot-permission'}
          >
            View all {reports.length} pending reports
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== HELPER COMPONENTS ====================

const WidgetSkeleton = ({ icon: Icon, title }) => (
  <Card className="h-full">
    <CardHeader className="pb-2">
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-gray-400" />
        <CardTitle className="text-base">{title}</CardTitle>
      </div>
    </CardHeader>
    <CardContent className="flex items-center justify-center h-32">
      <div className="animate-pulse text-muted-foreground">Loading...</div>
    </CardContent>
  </Card>
);

const WidgetError = ({ error }) => (
  <Card className="h-full border-red-200">
    <CardContent className="flex items-center justify-center h-full">
      <div className="text-red-500 text-sm">{error}</div>
    </CardContent>
  </Card>
);

// ==================== WIDGET REGISTRY ====================

export const WIDGET_COMPONENTS = {
  my_tasks_scores: MyTasksScoresWidget,
  todays_celebrations: TodaysCelebrationsWidget,
  todays_announcements: TodaysAnnouncementsWidget,
  action_required: ActionRequiredWidget,
  pending_gate_entries: PendingGateEntriesWidget,
  pending_indents: PendingIndentsWidget,
  pending_pos: PendingPOsWidget,
  pending_jc_issues: PendingJCIssuesWidget,
  pending_ot_reports: PendingOTReportsWidget
};

export const WIDGET_ENDPOINTS = {
  my_tasks_scores: '/api/dashboard/widgets/my-tasks-scores',
  todays_celebrations: '/api/dashboard/widgets/todays-celebrations',
  todays_announcements: '/api/dashboard/widgets/todays-announcements',
  action_required: '/api/dashboard/widgets/action-required',
  pending_gate_entries: '/api/dashboard/widgets/pending-gate-entries',
  pending_indents: '/api/dashboard/widgets/pending-indents',
  pending_pos: '/api/dashboard/widgets/pending-pos',
  pending_jc_issues: '/api/dashboard/widgets/pending-jc-issues',
  pending_ot_reports: '/api/ot-permissions/my-pending-reports'
};
